import { css, styled } from 'styled-components';
import { Input as AntdInput, InputProps as AntdInputProps, InputRef } from 'antd';
import { Text, TextType } from '../typography/Typography/Typography';
import React, { CSSProperties, useEffect, useRef } from 'react';
import { Flex, FlexDirections } from '../layout/Flex';

interface StyledInputProps {
  $addPadding?: boolean;
}

const getInputStyles = (addPadding?: boolean) => css`
  border-radius: 0.5rem;
  border: 1px solid #d9d9d9;
  font-size: 1.2rem;
  ${addPadding &&
  css`
    padding: 0.5rem 0.75rem;
  `}

  &:focus {
    outline: none;
  }
`;

export const StyledInput = styled(AntdInput)<StyledInputProps>`
  ${props => getInputStyles(props.$addPadding)}
`;
export const StyledInputPassword = styled(AntdInput.Password)<StyledInputProps>`
  ${props => getInputStyles(props.$addPadding)}
`;

export interface InputProps extends AntdInputProps, StyledInputProps {
  infoText?: string;
  infoTextType?: TextType;
  infoTextDirection?: FlexDirections;
  infoFlexGrow?: CSSProperties['flexGrow'];
  autoFocus?: boolean;
}

/**
 * Ant Design Input with additional text of different type to be displayed after
 * or below the input field.
 * @param infoText Text to be displayed
 * @param infoTextType Type of the text to indicate error or success
 * @param infoTextDirection Direction of the text (row or column to specify horizontal or vertical direction)
 * @param infoFlexGrow
 * @param rest
 * @constructor
 */
export const Input: React.FC<InputProps> = ({
  infoText,
  infoTextType,
  infoTextDirection = FlexDirections.ROW,
  infoFlexGrow = 0,
  autoFocus = false,
  ...rest
}) => {
  const inputRef = useRef<InputRef>(null);

  // focus the input field when the modal is opened to save the user a click
  // it is also natural to just start typing when dialog appears
  useEffect(() => {
    if (inputRef.current && autoFocus) {
      inputRef.current.focus();
    }
  }, [autoFocus, inputRef]);

  return (
    <Flex
      $flexWrap="nowrap"
      $alignItems="center"
      $gap="0.5rem"
      $flexDirection={infoTextDirection}
      $flexGrow={infoFlexGrow}
    >
      {rest.type === 'password' ? (
        <StyledInputPassword {...rest} ref={inputRef} />
      ) : (
        <StyledInput {...rest} ref={inputRef} />
      )}
      {infoText && <Text $type={infoTextType}>{infoText}</Text>}
    </Flex>
  );
};
